/** Persistent application rail: the four workflow stages inside a project
 * (Programme · Evidence · Test setup · Assessment), global links, demo banner. */
import { useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Link, NavLink, useLocation, useMatch } from "react-router-dom";

import { api } from "../api/client";
import { useBatches, useMe, useProject, useRuns } from "../api/hooks";
import type { Me } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { ConfirmDialog, ErrorNotice, Spinner } from "./ui";

export const can = (me: Me | undefined, action: string) => !!me?.permissions.includes(action);

function useStages(pid: string, me: Me) {
  const location = useLocation();
  const editor = can(me, "evidence.upload");
  const batches = useBatches(pid, editor);
  const runs = useRuns(pid, editor);
  const latestBatch = batches.data?.find((batch) => batch.processed);
  const latestRun = runs.data?.[0];
  const stages = [
    { label: "Programme", short: "Plan the annual SAT", to: `/p/${pid}`, enabled: true },
    { label: "Evidence", short: "Add source data", to: `/p/${pid}/evidence`, enabled: editor },
    {
      label: "Test setup",
      short: "Confirm assumptions",
      to: latestBatch ? `/p/${pid}/batches/${latestBatch.id}/setup` : `/p/${pid}/evidence`,
      enabled: editor && !!latestBatch,
      match: "/batches/",
    },
    {
      label: "Assessment",
      short: "Decide, investigate, retain",
      to: latestRun ? `/p/${pid}/runs/${latestRun.id}` : `/p/${pid}`,
      enabled: editor && !!latestRun,
      match: "/runs/",
    },
  ];
  const current = (stage: (typeof stages)[number]) =>
    stage.match ? location.pathname.includes(stage.match) : location.pathname === stage.to;
  return { editor, stages, current };
}

/** Phone widths hide the rail; the same destinations become a scrollable row. */
function MobileNav({ pid, me }: { pid: string | undefined; me: Me }) {
  return (
    <nav className="mobile-stages" aria-label="Navigation">
      <NavLink to="/" end className="btn small">
        All projects
      </NavLink>
      {pid && <MobileStages pid={pid} me={me} />}
      {me.org && can(me, "members.clients") && (
        <NavLink to="/org/members" className="btn small">
          Members
        </NavLink>
      )}
      {me.role === "admin" && (
        <NavLink to="/admin/orgs" className="btn small">
          Organisations
        </NavLink>
      )}
    </nav>
  );
}

function MobileStages({ pid, me }: { pid: string; me: Me }) {
  const { editor, stages, current } = useStages(pid, me);
  if (!editor) {
    return (
      <NavLink to={`/p/${pid}`} end className="btn small">
        Programme
      </NavLink>
    );
  }
  return (
    <>
      {stages.map((stage) => (
        <Link
          key={stage.label}
          to={stage.to}
          className={`btn small${current(stage) ? " active" : ""}${stage.enabled ? "" : " disabled"}`}
          aria-current={current(stage) ? "page" : undefined}
          aria-disabled={!stage.enabled}
        >
          {stage.label}
        </Link>
      ))}
      {can(me, "project.edit") && (
        <NavLink to={`/p/${pid}/settings`} className="btn small">
          Settings
        </NavLink>
      )}
    </>
  );
}

function ProjectStages({ pid, me }: { pid: string; me: Me }) {
  const project = useProject(pid);
  const { editor, stages, current } = useStages(pid, me);
  if (!editor) {
    return (
      <div className="sat-side-context">
        <span>Project</span>
        <strong>{project.data?.label ?? "…"}</strong>
        <small>{project.data?.client_name ?? ""}</small>
      </div>
    );
  }
  return (
    <>
      <div className="sat-side-context">
        <span>Active project</span>
        <strong>{project.data?.label ?? "…"}</strong>
        <small>{project.data?.client_name ?? ""}</small>
      </div>
      <nav aria-label="Workflow" className="stack" style={{ gap: "0.25rem" }}>
        <div className="side-section-title">Workflow</div>
        {stages.map((stage, index) => (
          <Link
            key={stage.label}
            to={stage.to}
            className={`stage-link${current(stage) ? " active" : ""}${stage.enabled ? "" : " disabled"}`}
            aria-current={current(stage) ? "page" : undefined}
            aria-disabled={!stage.enabled}
            title={stage.short}
          >
            <span className="num">{index + 1}</span>
            <span>
              {stage.label}
              <span className="sr-only"> — {stage.short}</span>
            </span>
          </Link>
        ))}
      </nav>
      {can(me, "project.edit") && (
        <NavLink to={`/p/${pid}/settings`} className="side-nav-link">
          Project settings
        </NavLink>
      )}
    </>
  );
}

function DemoBanner() {
  const client = useQueryClient();
  return (
    <div className="demo-banner" role="note">
      <strong>Demo environment.</strong> Uploaded evidence and analyses are removed automatically.
      <ConfirmDialog
        trigger={<button className="btn small">End demo session</button>}
        title="End the demo session?"
        description="Your uploaded evidence, analyses and projects in the demo are deleted now."
        confirmLabel="End session"
        danger
        onConfirm={async () => {
          await api.post("/demo/end-session");
          await client.invalidateQueries();
        }}
      />
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const me = useMe();
  const { signOut, user } = useAuth();
  const projectMatch = useMatch("/p/:pid/*");
  const pid = projectMatch?.params.pid;
  if (me.isLoading) {
    return (
      <div className="auth-page">
        <Spinner label="Loading your workspace" />
      </div>
    );
  }
  if (me.error) {
    return (
      <div className="auth-page">
        <div className="auth-card stack">
          <ErrorNotice error={me.error} title="No access" />
          <button className="btn" onClick={() => signOut()}>
            Sign out
          </button>
        </div>
      </div>
    );
  }
  const profile = me.data!;
  const brand = profile.org?.branding as { issuer_name?: string } | undefined;
  return (
    <>
      {profile.demo && <DemoBanner />}
      <header className="topbar">
        <Link to="/">
          <img src="/nsgs-logo.png" alt="NSGS" height={28} />
        </Link>
        <button className="btn small" onClick={() => signOut()}>
          Sign out
        </button>
      </header>
      <div className="shell">
        <aside className="shell-side" aria-label="Application">
          <Link to="/" className="side-logo" aria-label="All projects">
            <img src="/nsgs-logo.png" alt="NSGS" />
          </Link>
          <div className="sat-side-brand">
            <strong>Performance assurance</strong>
            <span>Commissioning test and 15 annual SATs</span>
          </div>
          <NavLink to="/" end className="side-nav-link">
            All projects
          </NavLink>
          {pid && <ProjectStages pid={pid} me={profile} />}
          <div className="side-section-title">Organisation</div>
          {profile.org && can(profile, "members.clients") && (
            <NavLink to="/org/members" className="side-nav-link">
              Members and clients
            </NavLink>
          )}
          {profile.role === "admin" && (
            <NavLink to="/admin/orgs" className="side-nav-link">
              Organisations (admin)
            </NavLink>
          )}
          <div className="side-user">
            <div>{profile.name ?? user?.email}</div>
            <div className="small">
              {profile.role === "admin" ? "Platform admin" : profile.role === "user" ? "User" : "Client (read-only)"}
              {profile.org ? ` · ${profile.org.name}` : ""}
            </div>
            {brand?.issuer_name && <div className="small">Reports issued by {brand.issuer_name}</div>}
            <button className="btn small" style={{ marginTop: "0.6rem" }} onClick={() => signOut()}>
              Sign out
            </button>
          </div>
        </aside>
        <main className="shell-main" id="main">
          <MobileNav pid={pid} me={profile} />
          {children}
        </main>
      </div>
    </>
  );
}
